/**
 * Ileara chrome panels — Menu + Search (Cart uses Horizon cart-drawer).
 * One panel open at a time; Esc / outside click closes.
 */
(() => {
  const root = document.querySelector('[data-ileara-chrome]');
  if (!root) return;

  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const menuToggle = root.querySelector('[data-chrome-toggle="mega"]');
  const menuLabel = menuToggle?.querySelector('.site-header__menu-label');
  const searchToggle = root.querySelector('[data-chrome-toggle="search"]');

  const panels = {
    mega: {
      el: root.querySelector('#site-mega'),
      toggle: menuToggle,
      focusSelector: '.mega__list a',
      sync(open) {
        if (!menuToggle) return;
        menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        if (menuLabel) menuLabel.textContent = open ? 'Close' : 'Menu';
      },
    },
    search: {
      el: root.querySelector('#site-search'),
      toggle: searchToggle,
      focusSelector: '#site-search-input',
      sync(open) {
        if (!searchToggle) return;
        searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        searchToggle.setAttribute('aria-label', open ? 'Close search' : 'Open search');
      },
    },
  };

  let activePanel = null;
  let closeTimer = 0;

  const setBodyLock = (locked) => {
    document.body.classList.toggle('chrome-panel-open', locked);
    document.body.classList.toggle('mega-open', locked);
  };

  const hidePanel = (name, { restoreFocus } = { restoreFocus: false }) => {
    const panel = panels[name];
    if (!panel?.el) return;

    panel.el.classList.remove('is-open');
    panel.el.setAttribute('aria-hidden', 'true');
    panel.sync?.(false);

    const finish = () => {
      panel.el.hidden = true;
      if (restoreFocus && panel.toggle) panel.toggle.focus();
    };

    if (mqReduce.matches) {
      finish();
      return;
    }

    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(finish, 240);
  };

  const showPanel = (name) => {
    const panel = panels[name];
    if (!panel?.el) return;

    window.clearTimeout(closeTimer);
    panel.el.hidden = false;
    panel.el.setAttribute('aria-hidden', 'false');
    setBodyLock(true);
    panel.sync?.(true);
    activePanel = name;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.el.classList.add('is-open');
        const focusEl = panel.el.querySelector(panel.focusSelector);
        if (focusEl instanceof HTMLElement) focusEl.focus();
      });
    });
  };

  const closeActive = ({ restoreFocus } = { restoreFocus: true }) => {
    if (!activePanel) {
      setBodyLock(false);
      return;
    }
    const name = activePanel;
    activePanel = null;
    hidePanel(name, { restoreFocus });
    setBodyLock(false);
  };

  const togglePanel = (name) => {
    if (activePanel === name) {
      closeActive({ restoreFocus: true });
      return;
    }
    if (activePanel) {
      hidePanel(activePanel, { restoreFocus: false });
      activePanel = null;
    }
    showPanel(name);
  };

  for (const [name, panel] of Object.entries(panels)) {
    panel.toggle?.addEventListener('click', () => togglePanel(name));
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activePanel) {
      event.preventDefault();
      closeActive({ restoreFocus: true });
    }
  });

  document.addEventListener('click', (event) => {
    if (!activePanel) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-ileara-chrome]')) return;
    closeActive({ restoreFocus: false });
  });

  root.querySelectorAll('.mega__list a').forEach((link) => {
    link.addEventListener('click', () => closeActive({ restoreFocus: false }));
  });

  const cartCountEls = root.querySelectorAll('.site-header__cart-count');
  const cartBtn = root.querySelector('.site-header__util--cart');

  if (cartCountEls.length) {
    /** @returns {number | null} */
    const readCountFromCartDrawer = () => {
      const hiddenCount = document.querySelector('cart-items-component [ref="cartItemCount"]');
      if (hiddenCount?.textContent) {
        const parsed = parseInt(hiddenCount.textContent.trim(), 10);
        if (!Number.isNaN(parsed)) return parsed;
      }

      const badge = document.querySelector('#cart-drawer-heading .theme-drawer__badge');
      if (badge?.textContent) {
        const parsed = parseInt(badge.textContent.trim(), 10);
        if (!Number.isNaN(parsed)) return parsed;
      }

      return null;
    };

    /** @param {number} count */
    const applyCount = (count) => {
      const safe = Math.max(0, count);

      cartCountEls.forEach((el) => {
        el.textContent = String(safe);
        el.classList.toggle('site-header__cart-count--empty', safe <= 0);
      });

      if (cartBtn) {
        cartBtn.setAttribute(
          'aria-label',
          safe > 0 ? `Open cart, ${safe} items` : 'Open cart'
        );
      }
    };

    /** @returns {Promise<number | null>} */
    const fetchCartItemCount = async () => {
      const cartUrl = window.Theme?.routes?.cart_url;
      if (!cartUrl) return null;

      try {
        const response = await fetch(`${cartUrl}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) return null;

        const data = await response.json();
        const count = data?.item_count;
        return typeof count === 'number' && !Number.isNaN(count) ? count : null;
      } catch {
        return null;
      }
    };

    /** Sync header count after cart mutations — never use detail.itemCount (qty added, not cart total). */
    const syncCartCount = async (totalQuantity) => {
      if (typeof totalQuantity === 'number' && !Number.isNaN(totalQuantity)) {
        applyCount(totalQuantity);
      }

      // Cart drawer morph may finish after the event promise; reconcile from section markup.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const fromDom = readCountFromCartDrawer();
      if (fromDom !== null) {
        applyCount(fromDom);
        return;
      }

      if (typeof totalQuantity !== 'number' || Number.isNaN(totalQuantity)) {
        const fromFetch = await fetchCartItemCount();
        if (fromFetch !== null) applyCount(fromFetch);
      }
    };

    /** @param {Event & { promise?: Promise<{ cart?: { totalQuantity?: number } }> }} event */
    const updateCartCount = (event) => {
      if (!(event.promise instanceof Promise)) return;

      event.promise
        .then(({ cart }) => syncCartCount(cart?.totalQuantity))
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[ileara-chrome] Cart count update failed:', error);
          }
        });
    };

    document.addEventListener('shopify:cart:lines-update', updateCartCount);
  }
})();
